import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Badge, Button, Form, Input, Pagination, Select, Skeleton, Tag } from "antd";
import {
  EnvironmentOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FilterOutlined,
  IdcardOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import AppShell from "../../components/app-shell/AppShell";
import Sheet from "../../components/app-shell/Sheet";
import useData from "../../datas/useData";
import "./member.css";
const BOOL_OPTIONS = [
  { label: "Any", value: "" },
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

const initialFilters = {
  q: "",
  blood: "",
  district: "",
  upuzila: "",
  country: "",
  profession: "",
  board: "",
  year: "",
  hobby: "",
  specialSkill: "",
  highSchool: "",
  businessLocation: "",
  available: "",
  livingAbroad: "",
  is_contact_public: "",
  is_address_public: "",
  is_education_public: "",
  hasPhoto: "",
  sortBy: "newest",
};

const maskPhone = (value = "") => {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (clean.length <= 6) return `${clean.slice(0, 1)}***${clean.slice(-1)}`;
  return `${clean.slice(0, 3)}****${clean.slice(-3)}`;
};

const maskEmail = (value = "") => {
  const email = String(value || "").trim();
  if (!email || !email.includes("@")) return email;
  const [local, domain] = email.split("@");
  if (!local || !domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${"*".repeat(Math.max(2, local.length - visible.length))}@${domain}`;
};

const Members = ({ endUser }) => {
  const navigate = useNavigate();
  const { getCommunityMembers, upsertConversation } = useUser();
  const { districts, bloods, upuzilas } = useData();
  const [filters, setFilters] = useState(initialFilters);
  const [draftFilters, setDraftFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [revealMap, setRevealMap] = useState({});
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pageSize = 24;

  // Search as you type, without a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.q === searchText ? prev : { ...prev, q: searchText }));
      setDraftFilters((prev) => ({ ...prev, q: searchText }));
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const { mutate: startConversationAction } = useMutation({
    mutationFn: upsertConversation,
    onSuccess: (conversation) => {
      if (!conversation?._id) return;
      navigate(`/messages/${conversation._id}`);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Unable to start chat");
    },
  });

  const upuzilaOptions = useMemo(() => {
    if (!draftFilters.district) return [];
    return upuzilas
      .filter(
        (x) =>
          String(x.district).toLowerCase() ===
          String(draftFilters.district).toLowerCase()
      )
      .map((x) => String(x.upazila).replace("Upazila", "").trim())
      .filter(Boolean);
  }, [draftFilters.district, upuzilas]);

  const { data, isLoading } = useQuery({
    queryKey: ["community-members", filters, page],
    queryFn: () =>
      getCommunityMembers({
        ...filters,
        page,
        limit: pageSize,
      }),
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  const members = data?.users || [];
  const total = data?.total || 0;

  const activeFilterCount = Object.keys(initialFilters).filter(
    (key) => key !== "q" && key !== "sortBy" && filters[key] !== initialFilters[key]
  ).length;

  const applyFilters = () => {
    setFilters(draftFilters);
    setPage(1);
    setFilterOpen(false);
  };

  const resetFilters = () => {
    const cleared = { ...initialFilters, q: searchText };
    setDraftFilters(cleared);
    setFilters(cleared);
    setPage(1);
    setFilterOpen(false);
  };

  const setDraft = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  };

  const toggleReveal = (memberId, field) => {
    if (!endUser?.isIdentityVerified) {
      setVerifyModalOpen(true);
      return;
    }
    const k = `${memberId}-${field}`;
    setRevealMap((prev) => ({ ...prev, [k]: !prev[k] }));
  };

  const renderContact = (member, field) => {
    const isPhone = field === "phone";
    const value = String(member?.[field] || "").trim();
    const isPublic = member?.is_contact_public !== false;

    if (!value) {
      return <span>N/A</span>;
    }
    if (!isPublic) {
      return <span>Private</span>;
    }

    const revealKey = `${member._id}-${field}`;
    const revealed = !!revealMap[revealKey];
    const display = revealed ? value : isPhone ? maskPhone(value) : maskEmail(value);

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <span style={{ color: "var(--app-ink)" }}>{display}</span>
        <Button
          type="text"
          size="small"
          aria-label={revealed ? "Hide" : "Show"}
          icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          onClick={() => toggleReveal(member._id, field)}
        />
      </span>
    );
  };

  const textField = (key, label) => (
    <Form.Item label={label}>
      <Input value={draftFilters[key]} onChange={(e) => setDraft(key, e.target.value)} />
    </Form.Item>
  );

  const yesNoField = (key, label) => (
    <Form.Item label={label}>
      <Select value={draftFilters[key]} onChange={(v) => setDraft(key, v)} options={BOOL_OPTIONS} />
    </Form.Item>
  );

  return (
    <AppShell title="Members" subtitle={isLoading ? "Loading…" : `${total} members`}>
      <div className="member-toolbar">
        <Input
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Name, phone, skill, place…"
          prefix={<SearchOutlined />}
        />
        <Badge count={activeFilterCount} size="small">
          <Button
            className="member-toolbar__filter"
            icon={<FilterOutlined />}
            onClick={() => {
              setDraftFilters(filters);
              setFilterOpen(true);
            }}
          >
            Filters
          </Button>
        </Badge>
      </div>

      {isLoading ? (
        <div className="feed-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="app-card">
              <Skeleton avatar active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="app-empty">No members found. Try a different search or clear the filters.</div>
      ) : (
        <div className="feed-list">
          {members.map((member) => (
            <div key={member._id} className="app-card member-card">
              <div className="member-card__head">
                <Avatar size={48} src={member?.profilePhoto || undefined} icon={<UserOutlined />} />
                <div className="member-card__who">
                  <div className="member-card__name">{member?.name || "Member"}</div>
                  <div className="member-card__meta">
                    <EnvironmentOutlined />{" "}
                    {[member?.upuzila, member?.district].filter(Boolean).join(", ") || "Location not set"}
                  </div>
                  {member?.profession ? <div className="member-card__meta">{member.profession}</div> : null}
                </div>
                <div className="member-card__blood">{member?.blood || "–"}</div>
              </div>

              <div>
                <Tag color={member?.available ? "green" : "orange"}>
                  {member?.available ? "Available to donate" : "Unavailable"}
                </Tag>
              </div>

              <div className="member-card__contact">
                <PhoneOutlined />
                {renderContact(member, "phone")}
              </div>
              <div className="member-card__contact">
                <MailOutlined />
                {renderContact(member, "email")}
              </div>

              <Button
                block
                icon={<MessageOutlined />}
                disabled={String(member._id) === String(endUser?._id)}
                onClick={() => startConversationAction({ participantId: member._id })}
              >
                Message
              </Button>
            </div>
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div className="feed-more">
          <Pagination simple current={page} onChange={(p) => setPage(p)} total={total} pageSize={pageSize} />
        </div>
      ) : null}

      <Sheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        height="85%"
        footer={
          <div className="sheet-actions">
            <Button size="large" onClick={resetFilters}>
              Reset
            </Button>
            <Button size="large" type="primary" onClick={applyFilters}>
              Show results
            </Button>
          </div>
        }
      >
        <h2 className="sheet-form__title" style={{ marginBottom: 12 }}>
          Filter members
        </h2>
        <Form layout="vertical" className="filter-grid">
          <Form.Item label="Blood">
            <Select
              value={draftFilters.blood}
              onChange={(v) => setDraft("blood", v)}
              options={[{ label: "Any", value: "" }, ...bloods]}
            />
          </Form.Item>
          <Form.Item label="Sort">
            <Select
              value={draftFilters.sortBy}
              onChange={(v) => setDraft("sortBy", v)}
              options={[
                { label: "Newest", value: "newest" },
                { label: "Oldest", value: "oldest" },
                { label: "Name A-Z", value: "name-asc" },
                { label: "Name Z-A", value: "name-desc" },
              ]}
            />
          </Form.Item>
          <Form.Item label="District">
            <Select
              showSearch
              value={draftFilters.district}
              onChange={(v) => setDraftFilters((prev) => ({ ...prev, district: v, upuzila: "" }))}
              options={[
                { label: "Any", value: "" },
                ...districts.map((d) => ({ label: d.name, value: d.name })),
              ]}
            />
          </Form.Item>
          <Form.Item label="Upazila">
            <Select
              showSearch
              value={draftFilters.upuzila}
              onChange={(v) => setDraft("upuzila", v)}
              options={[{ label: "Any", value: "" }, ...upuzilaOptions.map((u) => ({ label: u, value: u }))]}
            />
          </Form.Item>
          {textField("country", "Country")}
          {textField("profession", "Profession")}
          {textField("board", "Board")}
          {textField("year", "Year")}
          {textField("hobby", "Hobby")}
          {textField("specialSkill", "Special skill")}
          {textField("highSchool", "High school")}
          {textField("businessLocation", "Business location")}
          {yesNoField("available", "Available")}
          {yesNoField("livingAbroad", "Living abroad")}
          {yesNoField("is_contact_public", "Contact public")}
          {yesNoField("is_address_public", "Address public")}
          {yesNoField("is_education_public", "Education public")}
          {yesNoField("hasPhoto", "Has photo")}
        </Form>
      </Sheet>

      <Sheet open={verifyModalOpen} onClose={() => setVerifyModalOpen(false)}>
        <div className="sheet-form">
          <IdcardOutlined className="sheet-form__done-icon" />
          <h2 className="sheet-form__title">Verification required</h2>
          <p className="sheet-form__hint" style={{ margin: 0 }}>
            You need identity verification before viewing full contact details. Submit any one of: Govt ID,
            driving licence or passport.
          </p>
          <p className="sheet-form__hint" style={{ margin: 0 }}>
            After approval, tap the eye icon to see a member's full phone and email.
          </p>
          <Button size="large" block onClick={() => setVerifyModalOpen(false)}>
            OK
          </Button>
        </div>
      </Sheet>
    </AppShell>
  );
};

export default Members;

